import React, { useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { 
  Archive, ArchiveRestore, AlertTriangle, Loader2, 
  Calendar, FileText, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { formatDate, getTodayLocal } from '../lib/dateUtils';

/**
 * ClientArchiveModal - Modal for archiving a client
 */
export function ClientArchiveModal({ 
  client, 
  isOpen, 
  onClose, 
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('retainer_ended');
  const [notes, setNotes] = useState('');
  const [handleTasks, setHandleTasks] = useState('on_hold');

  const handleArchive = async () => {
    setLoading(true);
    try {
      const response = await adminApi.post(`/api/admin/clients/${client._id || client.id}/archive`, {
        reason,
        notes,
        handle_tasks: handleTasks
      });
      
      toast.success(response.data.message || 'Client archived successfully');
      onComplete && onComplete();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive client');
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  const clientName = client.company_name || client.name || 'Client';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-orange-500" />
            Archive Client: {clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Warning */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-orange-800">Archiving will:</p>
                <ul className="mt-1 text-orange-700 space-y-1">
                  <li>• Remove client from active lists and dashboards</li>
                  <li>• Stop recurring invoice generation</li>
                  <li>• Preserve all historical data (tasks, invoices, etc.)</li>
                  <li>• Client can be restored at any time</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Reason for archiving *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
              data-testid="archive-reason-select"
            >
              <option value="retainer_ended">Retainer Ended</option>
              <option value="project_completed">Project Completed</option>
              <option value="client_request">Client Request</option>
              <option value="manual">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about why this client is being archived..."
              className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30"
              rows={3}
            />
          </div>

          {/* Task handling */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              What should happen to active tasks?
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-xl cursor-pointer hover:bg-white/60 transition-colors">
                <input
                  type="radio"
                  name="handleTasks"
                  value="on_hold"
                  checked={handleTasks === 'on_hold'}
                  onChange={(e) => setHandleTasks(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-[#1a1a2e]">Set to &quot;On Hold&quot;</p>
                  <p className="text-sm text-neutral-500">Tasks will be paused and can be resumed if client is restored (recommended)</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 border rounded-xl cursor-pointer hover:bg-white/60 transition-colors">
                <input
                  type="radio"
                  name="handleTasks"
                  value="complete_all"
                  checked={handleTasks === 'complete_all'}
                  onChange={(e) => setHandleTasks(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-[#1a1a2e]">Mark all as &quot;Completed&quot;</p>
                  <p className="text-sm text-neutral-500">Only if all work is actually finished</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 border rounded-xl cursor-pointer hover:bg-white/60 transition-colors">
                <input
                  type="radio"
                  name="handleTasks"
                  value="keep_active"
                  checked={handleTasks === 'keep_active'}
                  onChange={(e) => setHandleTasks(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-[#1a1a2e]">Keep tasks active</p>
                  <p className="text-sm text-neutral-500">Tasks will remain in current status</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleArchive}
            disabled={loading}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="confirm-archive-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Archiving...
              </>
            ) : (
              <>
                <Archive className="w-4 h-4 mr-2" />
                Archive Client
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ClientRestoreModal - Modal for restoring an archived client
 */
export function ClientRestoreModal({ 
  client, 
  isOpen, 
  onClose, 
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [reactivateTasks, setReactivateTasks] = useState(true);
  const [newRetainerEndDate, setNewRetainerEndDate] = useState('');

  const handleRestore = async () => {
    setLoading(true);
    try {
      const response = await adminApi.post(`/api/admin/clients/${client.id}/restore`, {
        notes,
        reactivate_tasks: reactivateTasks,
        new_retainer_end_date: newRetainerEndDate || null
      });
      
      toast.success(response.data.message || 'Client restored successfully');
      onComplete && onComplete();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore client');
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  const clientName = client.company_name || client.name || 'Client';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArchiveRestore className="w-5 h-5 text-green-500" />
            Restore Client: {clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-green-800">Restoring will:</p>
                <ul className="mt-1 text-green-700 space-y-1">
                  <li>• Return client to active lists and dashboards</li>
                  <li>• Allow creating new tasks and invoices</li>
                  <li>• Optionally reactivate on-hold tasks</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Archive info */}
          {client.archived_at && (
            <div className="bg-white/40 rounded-xl p-3 text-sm">
              <p className="text-neutral-600">
                <span className="font-medium">Archived:</span> {formatDate(client.archived_at)}
              </p>
              <p className="text-neutral-600">
                <span className="font-medium">Reason:</span> {client.archived_reason?.replace(/_/g, ' ') || 'Not specified'}
              </p>
              {client.on_hold_tasks > 0 && (
                <p className="text-neutral-600">
                  <span className="font-medium">Tasks on hold:</span> {client.on_hold_tasks}
                </p>
              )}
            </div>
          )}

          {/* Reactivate tasks */}
          {client.on_hold_tasks > 0 && (
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reactivateTasks}
                  onChange={(e) => setReactivateTasks(e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                />
                <div>
                  <span className="font-medium text-[#1a1a2e]">Reactivate {client.on_hold_tasks} on-hold tasks</span>
                  <p className="text-sm text-neutral-500">Tasks will be restored to their previous status</p>
                </div>
              </label>
            </div>
          )}

          {/* New retainer end date */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              New Retainer End Date (optional)
            </label>
            <input
              type="date"
              value={newRetainerEndDate}
              onChange={(e) => setNewRetainerEndDate(e.target.value)}
              min={getTodayLocal()}
              className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-green-500"
            />
            <p className="text-xs text-neutral-500 mt-1">Leave empty to keep existing retainer dates</p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Client renewed contract for 2026..."
              className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-green-500"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRestore}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
            data-testid="confirm-restore-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Restoring...
              </>
            ) : (
              <>
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Restore Client
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ExpiringRetainersAlert - Shows clients with expiring retainers
 */
export function ExpiringRetainersAlert({ clients, onArchive, onDismiss }) {
  if (!clients || clients.length === 0) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-medium text-yellow-800">
            {clients.length} client{clients.length !== 1 ? 's' : ''} with retainer{clients.length !== 1 ? 's' : ''} expiring soon
          </h4>
          <div className="mt-3 space-y-2">
            {clients.map(client => (
              <div 
                key={client.id} 
                className="flex items-center justify-between bg-white rounded-xl p-3 border border-yellow-100"
              >
                <div className="flex items-center gap-3">
                  {client.color && (
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: client.color }}
                    />
                  )}
                  <div>
                    <p className="font-medium text-[#1a1a2e]">{client.company_name || client.name}</p>
                    <p className="text-sm text-neutral-500">
                      Expires {client.retainer_end_date} ({client.days_until_expiry} days) • 
                      {client.currency} {client.retainer_value?.toLocaleString()}/mo
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onArchive(client)}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    Archive
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDismiss(client.id)}
                    className="text-neutral-500"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
